import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { comics, type Comic } from "@/mocks/comics";
import { categoryMap } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [showFullContent, setShowFullContent] = useState(false);

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const catName = categoryMap[comic.category] || comic.category;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: comic.title,
              author: { "@type": "Person", name: comic.author },
              description: comic.description,
              datePublished: comic.updateDate,
              publisher: { "@type": "Organization", name: "4399漫画网", url: "https://www.4399mh.com" },
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://www.4399mh.com/" },
                { "@type": "ListItem", position: 2, name: catName, item: `https://www.4399mh.com/category/${comic.category}` },
                { "@type": "ListItem", position: 3, name: comic.title },
              ],
            },
          ]),
        }}
      />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: catName, href: `/category/${comic.category}` },
            { label: comic.title },
          ]} />
        </div>

        {/* Article Header */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <article className="flex flex-col lg:flex-row gap-6 md:gap-10 py-6 md:py-10">
            {/* Cover */}
            <div className="flex-shrink-0 w-full sm:w-[280px] mx-auto lg:mx-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩国漫画无删减下拉式阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === "连载中"
                    ? "bg-primary-100 text-primary-700"
                    : "bg-accent-100 text-accent-700"
                }`}>
                  {comic.status}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="p-3 rounded-lg bg-background-100">
                  <p className="text-xs text-foreground-400 mb-0.5">作者</p>
                  <p className="text-sm font-medium text-foreground-800">{comic.author}</p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <p className="text-xs text-foreground-400 mb-0.5">评分</p>
                  <p className="text-sm font-medium text-foreground-800 flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    {comic.rating}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <p className="text-xs text-foreground-400 mb-0.5">人气</p>
                  <p className="text-sm font-medium text-foreground-800">
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-background-100">
                  <p className="text-xs text-foreground-400 mb-0.5">分类</p>
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200"
                  >
                    {catName}
                  </Link>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
                <button className="flex-1 sm:flex-none px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
                  <i className="ri-play-circle-line mr-2"></i>开始阅读
                </button>
                <button className="flex-1 sm:flex-none px-8 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap">
                  <i className="ri-bookmark-line mr-2"></i>加入书架
                </button>
                <button className="flex-1 sm:flex-none px-8 py-3 rounded-full border-2 border-background-200 text-foreground-600 text-sm font-semibold hover:border-primary-300 hover:text-primary-500 transition-all duration-200 whitespace-nowrap">
                  <i className="ri-share-forward-line mr-2"></i>分享
                </button>
              </div>

              <div className="p-4 rounded-lg bg-background-100 mb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div className="flex items-center gap-6 text-xs text-foreground-400">
                <span>共 {comic.chapters} 话</span>
                <span>更新于 {comic.updateDate}</span>
              </div>
            </div>
          </article>
        </div>

        {/* Content Preview */}
        {comic.content && (
          <section className="bg-background-100/70 py-8 md:py-10">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                内容预览
              </h2>
              <div className="bg-background-50 rounded-xl p-6 md:p-8">
                <div className={`prose prose-sm max-w-none text-foreground-700 leading-relaxed ${
                  !showFullContent ? "max-h-48 overflow-hidden relative" : ""
                }`}>
                  <p className="whitespace-pre-line">{comic.content}</p>
                  {!showFullContent && (
                    <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50 to-transparent"></div>
                  )}
                </div>
                <button
                  onClick={() => setShowFullContent(!showFullContent)}
                  className="mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap"
                >
                  {showFullContent ? "收起内容" : "展开全部内容"}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Chapter List */}
        <section className="py-8 md:py-10">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
              章节列表
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => comic.chapters - i).map((ch) => (
                <button
                  key={ch}
                  className="px-4 py-2.5 rounded-lg bg-background-100 hover:bg-primary-50 border border-transparent hover:border-primary-200 text-sm text-foreground-700 hover:text-primary-600 transition-all duration-200 text-center whitespace-nowrap"
                >
                  第{ch}话
                </button>
              ))}
              {comic.chapters > 20 && (
                <button className="px-4 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-400 text-center whitespace-nowrap">
                  更多章节...
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="bg-background-100/70 py-8 md:py-10">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.category}`}
                  className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                >
                  更多{catName} <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}